export type MarketingFeature = {
  title: string;
  description: string;
  icon: string;
};

export type MarketingFaqItem = {
  question: string;
  answer: string;
};

export type MarketingWorkspaceFixture = {
  title: string;
  category: string;
  author: string;
  description: string;
  rating: string;
  reviewCount?: string;
  installs: string;
  productId?: string;
  priceLabel?: string;
  priceAmount?: number;
  status?: string;
  slug?: string;
  imageUrl?: string;
  mediaPreview?: {
    kind: "agent-conversation";
    agentName: string;
    statusLabel: string;
    messages: {
      role: "user" | "assistant";
      text: string;
    }[];
  };
};

export type MarketingBlogFixture = {
  title: string;
  description: string;
  publishedAt: string;
  slug: string;
};

export type MarketingPricingTier = {
  slug: "spark" | "forge" | "blaze" | "inferno";
  title: string;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  annualLabel?: string;
  badge?: string;
  features: string[];
  mapsTo: "starter" | "pro";
};

export const marketingNavigation = [
  { title: "Marketplace", href: "/listings" },
  { title: "Pricing", href: "/pricing" },
  { title: "Features", href: "/features" },
  { title: "Blog", href: "/blog" },
  { title: "About", href: "/about" },
];

export const marketingFooterColumns = [
  {
    title: "Product",
    items: [
      { title: "Marketplace", href: "/listings" },
      { title: "Features", href: "/features" },
    ],
  },
  {
    title: "Resources",
    items: [{ title: "Blog", href: "/blog" }],
  },
  {
    title: "Company",
    items: [
      { title: "About", href: "/about" },
      { title: "Contact", href: `mailto:help@clawagora.com` },
    ],
  },
  {
    title: "Legal",
    items: [
      { title: "Privacy Policy", href: "/privacy-policy" },
      { title: "Terms of Service", href: "/terms-of-service" },
    ],
  },
];

export const homeFaq: MarketingFaqItem[] = [
  {
    question: "Is OpenClaw Marketplace an official OpenClaw service?",
    answer:
      "No. OpenClaw Marketplace is an independent platform operated by ClawAgora. It is built for hosted OpenClaw agents, skills, and workspaces, but it is not the official OpenClaw project.",
  },
  {
    question: "What can I buy on OpenClaw Marketplace?",
    answer:
      "You can browse and buy OpenClaw agents, skills, and operator-ready workspaces, then manage what you purchased from one hosted marketplace account.",
  },
  {
    question: "What happens after I buy a listing?",
    answer:
      "Purchased listings show up in your library, where you can review versions, access install instructions, and return to the marketplace when you need more capabilities.",
  },
  {
    question: "Do creators need to self-host everything themselves?",
    answer:
      "No. The platform is positioned around hosted OpenClaw workflows, so buyers do not need to assemble a self-hosted stack just to start using marketplace listings.",
  },
  {
    question: "Can creators publish their own OpenClaw listings?",
    answer:
      "Yes. Sellers can onboard, connect payments, publish listings, and manage updates so buyers can discover install-ready OpenClaw packages in one place.",
  },
];

export const pricingFaq: MarketingFaqItem[] = [
  {
    question: "What is included in each plan?",
    answer:
      "Every plan includes hosted OpenClaw access, marketplace browsing, installs, and managed infrastructure. Higher tiers expand compute, message volume, and operator support.",
  },
  {
    question: "Can I upgrade or downgrade my plan?",
    answer:
      "Plan switching is not available yet but is coming soon. Please choose your initial plan carefully. We will notify existing subscribers when this feature launches.",
  },
  {
    question: "Is there a free trial?",
    answer:
      "We do not offer a free trial, but plans include a 14-day refund window so you can evaluate whether the hosted OpenClaw workflow fits your needs.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept all major credit and debit cards through Stripe, including Visa, Mastercard, and American Express. All payments are processed securely.",
  },
  {
    question: "What happens when I exceed my message quota?",
    answer:
      "When you hit your monthly quota, your hosted workspace remains available, but AI-assisted activity pauses until the next billing cycle or a higher plan is selected.",
  },
];

export const homeHeroStats = [
  { value: "163K+", label: "GitHub Stars" },
  { value: "5,700+", label: "Community Skills" },
  { value: "Deploy in Minutes", label: "One-click instance provisioning" },
];

export const homeFeatures: MarketingFeature[] = [
  {
    title: "Hosted OpenClaw",
    description:
      "Launch into a hosted OpenClaw environment without stitching together your own infrastructure first.",
    icon: "server",
  },
  {
    title: "Browse Agents, Skills, and Workspaces",
    description:
      "Explore operator-ready listings built for real OpenClaw workflows, from lightweight skills to fuller workspace packages.",
    icon: "users",
  },
  {
    title: "One-Click Installs",
    description:
      "Install marketplace listings into your OpenClaw setup with less manual configuration and less trial-and-error.",
    icon: "download",
  },
  {
    title: "Operator-Ready Detail Pages",
    description:
      "Review what each listing does, how it is positioned, and whether it matches your workflow before you buy.",
    icon: "message-square",
  },
  {
    title: "Clean Buyer Flows",
    description:
      "Move from discovery to purchase with clearer account, checkout, and library flows built around marketplace usage.",
    icon: "shield",
  },
  {
    title: "Customizable Delivery",
    description:
      "OpenClaw listings are built to be adapted, so teams can extend what they buy instead of starting from scratch.",
    icon: "code",
  },
  {
    title: "Versioned Listings",
    description:
      "Track listing evolution over time and keep updates tied to something buyers can actually inspect.",
    icon: "layers",
  },
  {
    title: "Security-Minded Platform Flows",
    description:
      "Authentication, payments, and access control are kept explicit so marketplace actions stay auditable.",
    icon: "lock",
  },
];

export const homeWorkflowSteps = [
  {
    step: "01",
    title: "Browse the Marketplace",
    description:
      "Start with the OpenClaw agents, skills, and workspaces that match the operator problem you need to solve.",
    icon: "sparkles",
  },
  {
    step: "02",
    title: "Choose Your Hosted Setup",
    description:
      "Pick the hosted OpenClaw plan that gives you the compute, messages, and marketplace access you need.",
    icon: "rocket",
  },
  {
    step: "03",
    title: "Install and Operate",
    description:
      "Buy, install, and manage listings from a single OpenClaw Marketplace account instead of juggling scattered tooling.",
    icon: "message-circle-more",
  },
];

export const homeTestimonials = [
  {
    quote:
      "I found a usable OpenClaw setup faster because the marketplace showed me what was already packaged and install-ready.",
    author: "Chris Mercer",
    role: "Indie Developer",
  },
  {
    quote:
      "The listing pages made OpenClaw workflows feel like products instead of loose files and vague promises.",
    author: "Lena Brooks",
    role: "Product Operator",
  },
  {
    quote:
      "Hosted access, marketplace discovery, and post-purchase management finally sit in one place instead of a patchwork.",
    author: "Jordan Price",
    role: "Founder",
  },
];

export const pricingTiers: MarketingPricingTier[] = [
  {
    slug: "spark",
    title: "Spark",
    description:
      "Entry plan for exploring OpenClaw Marketplace with lighter usage and hosted access.",
    monthlyPrice: "$29.90",
    annualPrice: "$24.90",
    annualLabel: "Annual Save 17%",
    features: [
      "Your OpenClaw 1 OCPU / 4 GB",
      "300 AI messages/month",
      "Basic models",
      "3 workspace previews/month",
      "Standard support",
    ],
    mapsTo: "starter",
  },
  {
    slug: "forge",
    title: "Forge",
    description:
      "Balanced plan for teams actively buying, installing, and running marketplace listings.",
    monthlyPrice: "$59.90",
    annualPrice: "$49.90",
    annualLabel: "Annual Save 17%",
    features: [
      "Your OpenClaw 2 OCPU / 8 GB",
      "1,500 AI messages/month",
      "Basic + Standard models",
      "10 workspace previews/month",
      "Standard support",
    ],
    mapsTo: "starter",
  },
  {
    slug: "blaze",
    title: "Blaze",
    description:
      "Higher-throughput plan for operators who need more compute, more messages, and broader marketplace usage.",
    monthlyPrice: "$109.00",
    annualPrice: "$89.00",
    annualLabel: "Annual Save 18%",
    badge: "Most Popular",
    features: [
      "Your OpenClaw 4 OCPU / 16 GB",
      "5,000 AI messages/month",
      "All models (Basic + Standard + Premium)",
      "Unlimited workspace previews",
      "1 featured listing/month",
      "Priority support",
    ],
    mapsTo: "pro",
  },
  {
    slug: "inferno",
    title: "Inferno",
    description:
      "Highest-capacity plan for heavier OpenClaw Marketplace workflows and dedicated support needs.",
    monthlyPrice: "$239.00",
    annualPrice: "$199.00",
    annualLabel: "Annual Save 16%",
    features: [
      "Your OpenClaw 4 OCPU / 16 GB",
      "15,000 AI messages/month",
      "All models (Basic + Standard + Premium)",
      "Unlimited workspace previews",
      "3 featured listings/month",
      "Dedicated support",
    ],
    mapsTo: "pro",
  },
];

export const featurePageSections: MarketingFeature[] = [
  {
    title: "Hosted OpenClaw Environments",
    description:
      "Start from a hosted OpenClaw setup instead of rolling your own deployment before you can evaluate marketplace listings.",
    icon: "server",
  },
  {
    title: "Marketplace Discovery",
    description:
      "Browse OpenClaw agents, skills, and workspaces with category-based discovery instead of scavenging across multiple registries and docs.",
    icon: "download",
  },
  {
    title: "One-Click Installs",
    description:
      "Move from discovery to install with less setup friction, so buyers can evaluate real OpenClaw packages faster.",
    icon: "download",
  },
  {
    title: "Operator-Focused Listing Pages",
    description:
      "Each listing page is designed to explain what the package does, who it is for, and how it fits into a real workflow before purchase.",
    icon: "message-square",
  },
  {
    title: "Buyer and Seller Flows",
    description:
      "Marketplace account flows keep browsing, buying, account access, and post-purchase management structured around actual marketplace usage.",
    icon: "shield",
  },
  {
    title: "Creator-Ready Marketplace",
    description:
      "Creators can publish listings buyers can discover, compare, purchase, and revisit through a single OpenClaw Marketplace surface.",
    icon: "users",
  },
  {
    title: "Customizable Delivery",
    description:
      "Bought listings are meant to be a starting point teams can adapt to their own OpenClaw operating model.",
    icon: "code",
  },
  {
    title: "Versioned Marketplace Assets",
    description:
      "Version history makes it easier to understand what changed in a listing instead of treating every update like a black box.",
    icon: "layers",
  },
  {
    title: "Security-Minded Operations",
    description:
      "Authentication, payment, and access flows are explicit so the hosted marketplace experience stays understandable and controlled.",
    icon: "lock",
  },
];

export const aboutPagePricingTable = [
  ["Spark", "$29.90", "$299", "1 OCPU", "4 GB", "300", "Basic"],
  ["Forge", "$59.90", "$599", "2 OCPU", "8 GB", "1,500", "Standard"],
  ["Blaze", "$109", "$1,090", "4 OCPU", "16 GB", "5,000", "Premium"],
  ["Inferno", "$239", "$2,390", "4 OCPU", "16 GB", "15,000", "Premium, 2 instances"],
];

export const blogFixtures: MarketingBlogFixture[] = [
  {
    title: "What I Removed Before Selling My Workspace (And What I Kept)",
    description:
      "The sanitization process for turning a personal OpenClaw workspace into a sellable template — where the line is between secret sauce and shareable value.",
    publishedAt: "March 24, 2026",
    slug: "what-i-removed-before-selling-my-workspace",
  },
  {
    title: "I Packaged My Personal Workspace Into a Product in One Afternoon",
    description:
      "A real walkthrough of turning a personal OpenClaw workflow into a sellable listing on OpenClaw Marketplace — what stayed, what got cut, and what mattered to buyers.",
    publishedAt: "March 17, 2026",
    slug: "i-packaged-my-workspace-in-one-afternoon",
  },
  {
    title: "5 Workspace Patterns That Separate Beginners From Power Users",
    description:
      "Advanced OpenClaw workspace patterns that make the difference between a basic setup and a production-grade workflow.",
    publishedAt: "March 15, 2026",
    slug: "5-workspace-patterns-power-users",
  },
  {
    title: "The Anatomy of a High-Quality Workspace Template",
    description:
      "What makes a workspace template worth paying for — structure, documentation, configurability, and the details that separate good from great.",
    publishedAt: "March 15, 2026",
    slug: "anatomy-of-a-high-quality-workspace-template",
  },
  {
    title: "Building a Workspace That Works for Someone Else, Not Just You",
    description:
      "The mental shift from personal tool to shareable product — how to build OpenClaw workspaces that other developers can actually use.",
    publishedAt: "March 15, 2026",
    slug: "building-a-workspace-that-works-for-someone-else",
  },
  {
    title: "Developer Side Income: Workspaces vs. Courses vs. SaaS vs. Freelancing",
    description:
      "An honest comparison of developer monetization channels — where selling workspace templates fits on the effort-to-reward spectrum.",
    publishedAt: "March 15, 2026",
    slug: "developer-side-income-workspaces-vs-courses-vs-saas",
  },
  {
    title: "How Version Updates Work: Selling a Workspace Is Not a One-Time Thing",
    description:
      "Marketplace listings are not static products. Here is how versioned OpenClaw packages stay useful after the first purchase.",
    publishedAt: "March 15, 2026",
    slug: "how-version-updates-work",
  },
  {
    title: "Seller Zero: Why I Listed on a Marketplace With No Track Record",
    description:
      "The story of an early OpenClaw Marketplace seller — why they listed before the platform had much proof, and what they learned from the first buyers.",
    publishedAt: "March 15, 2026",
    slug: "seller-zero-why-i-listed-on-a-marketplace-with-no-track-record",
  },
  {
    title: "The $50 Deposit: Why We Charge Sellers and What It Filters Out",
    description:
      "A counterintuitive seller decision on OpenClaw Marketplace — why a listing deposit can improve quality without turning the platform into a graveyard of spam.",
    publishedAt: "March 15, 2026",
      slug: "why-we-charge-sellers-50-deposit",
  },
  {
    title: "Best OpenClaw Hosting Platforms Compared (2026)",
    description:
      "An honest comparison of OpenClaw hosting options in 2026 — managed marketplaces, registries, and self-hosting paths with tradeoffs on setup, pricing, and control.",
    publishedAt: "March 11, 2026",
    slug: "best-openclaw-hosting-platforms-compared",
  },
  {
    title: "OpenClaw Marketplace vs Self-Hosting OpenClaw: Full Cost and Effort Breakdown",
    description:
      "A detailed comparison of using OpenClaw Marketplace versus self-hosting OpenClaw on your own VPS — covering real costs, setup effort, maintenance burden, and total cost of ownership.",
    publishedAt: "March 10, 2026",
    slug: "openclaw-marketplace-vs-self-hosting-openclaw",
  },
  {
    title: "From ZIP Uploads to MCP: Our Agent-Native Approach",
    description:
      "How we're replacing manual workspace uploads with a direct agent-to-marketplace connection using MCP — and why this matters for the future of AI agent marketplaces.",
    publishedAt: "March 10, 2026",
    slug: "from-zip-uploads-to-mcp-agent-native",
  },
  {
    title: "OpenClaw Marketplace vs ClawHub: What's the Difference?",
    description:
      "OpenClaw Marketplace and ClawHub solve different problems. ClawHub is a registry, while OpenClaw Marketplace is a hosted marketplace flow. Here is where each one fits.",
    publishedAt: "March 9, 2026",
    slug: "openclaw-marketplace-vs-clawhub",
  },
  {
    title: "Marketplace vs DIY OpenClaw Stack: Which Path Should You Choose?",
    description:
      "A direct comparison between buying through a hosted marketplace and stitching together your own OpenClaw stack from separate tools and hosting providers.",
    publishedAt: "March 8, 2026",
    slug: "marketplace-vs-diy-openclaw-stack",
  },
  {
    title: "OpenClaw Marketplace vs ClawMart: Comparing Two OpenClaw Marketplaces",
    description:
      "OpenClaw Marketplace and ClawMart both help buyers find OpenClaw assets, but they differ in scope, hosting assumptions, and purchase flow.",
    publishedAt: "March 7, 2026",
    slug: "openclaw-marketplace-vs-clawmart",
  },
  {
    title: "How to Run OpenClaw Without Self-Hosting: Every Managed Option in 2026",
    description:
      "You do not need a VPS or Linux skills to run OpenClaw. This guide compares the main managed options so you can choose the right one without touching a server.",
    publishedAt: "March 6, 2026",
    slug: "how-to-run-openclaw-without-self-hosting",
  },
  {
    title: "OpenClaw Setup Guide: Self-Hosted vs Managed Hosting (2026)",
    description:
      "A side-by-side walkthrough of setting up OpenClaw two ways — self-hosted on a VPS and through a hosted marketplace path.",
    publishedAt: "March 5, 2026",
    slug: "openclaw-setup-guide-self-hosted-vs-managed",
  },
  {
    title: "How to Install OpenClaw Skills with OpenClaw Marketplace",
    description:
      "A step-by-step guide to finding, installing, and managing OpenClaw skills from OpenClaw Marketplace and adjacent ecosystem sources.",
    publishedAt: "March 4, 2026",
    slug: "how-to-install-openclaw-skills-with-openclaw-marketplace",
  },
];

export const homeWorkspaceFixtures: MarketingWorkspaceFixture[] = [
  {
    title: "WebSpider Lite",
    category: "Web Scraping",
    author: "Operator Foundry",
    description:
      "Lightweight web scraping framework with anti-detection features. Supports JavaScript rendering, proxy rotation, and CAPTCHA solving. Export to JSON, CSV, or database.",
    rating: "3.5",
    reviewCount: "2",
    installs: "203",
    productId: "marketing-home-webspider-lite",
    priceLabel: "$9",
    priceAmount: 9,
    slug: "webspider-lite",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "webspider-lite",
      statusLabel: "live",
      messages: [
        {
          role: "user",
          text: "Scrape 25 fintech pricing pages. Capture plan names, pricing tiers, and contact emails.",
        },
        {
          role: "assistant",
          text: "Rendering JavaScript pages, rotating proxies, and handling anti-bot checks across the target list...",
        },
        {
          role: "assistant",
          text: "✓ Done. 25 sites scraped. 184 pricing rows and 37 contact emails exported to CSV and JSON.",
        },
      ],
    },
  },
  {
    title: "AutoFlow Pro",
    category: "Automation",
    author: "Workflow Forge",
    description:
      "Enterprise-grade workflow automation with visual pipeline builder. Supports 50+ integrations including Slack, Jira, and GitHub. Features conditional logic, error handling, and retry mechanisms.",
    rating: "4.5",
    reviewCount: "2",
    installs: "142",
    productId: "marketing-home-autoflow-pro",
    priceLabel: "$19",
    priceAmount: 19,
    slug: "autoflow-pro",
    imageUrl: "/images/easyclaw/use-case-3-automation.webp",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "autoflow-pro",
      statusLabel: "live",
      messages: [
        {
          role: "user",
          text: "When a Stripe payment fails, open a Jira ticket, notify Slack, and queue a retry sequence.",
        },
        {
          role: "assistant",
          text: "Watching Stripe webhooks, mapping the failed account to Jira ownership, and drafting the Slack escalation now...",
        },
        {
          role: "assistant",
          text: "✓ Workflow armed. Jira ticket created, Slack posted, and retry automation scheduled for the next billing window.",
        },
      ],
    },
  },
  {
    title: "TestRunner Pro",
    category: "Testing",
    author: "Shiproom Labs",
    description:
      "Comprehensive testing framework with parallel execution. Features snapshot testing, code coverage, and CI integration. Supports unit, integration, and E2E tests.",
    rating: "4.0",
    reviewCount: "0",
    installs: "112",
    productId: "marketing-home-testrunner-pro",
    priceLabel: "$39",
    priceAmount: 39,
    slug: "testrunner-pro",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "testrunner-pro",
      statusLabel: "live",
      messages: [
        {
          role: "user",
          text: "Run regression on checkout, auth, and dashboard flows before tonight's release candidate.",
        },
        {
          role: "assistant",
          text: "Booting parallel browser suites, collecting console logs, and checking snapshot drift across the critical paths...",
        },
        {
          role: "assistant",
          text: "✓ Regression complete. 126 tests passed, 2 flaky specs isolated, and the release report is ready for review.",
        },
      ],
    },
  },
  {
    title: "DataCrunch Toolkit",
    category: "Data Analysis",
    author: "Signal Atelier",
    description:
      "High-performance data analysis toolkit with pandas-like API. Includes built-in visualizations, statistical tests, and export to Excel/CSV/JSON. Perfect for data scientists and analysts.",
    rating: "5.0",
    reviewCount: "1",
    installs: "89",
    productId: "marketing-home-datacrunch-toolkit",
    priceLabel: "$9",
    priceAmount: 9,
    slug: "datacrunch-toolkit",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "datacrunch-toolkit",
      statusLabel: "live",
      messages: [
        {
          role: "user",
          text: "Compare Q1 vs Q2 lead quality, conversion rates, and CAC by acquisition channel.",
        },
        {
          role: "assistant",
          text: "Pulling cohort data, normalizing spend by channel, and running variance checks on conversion quality...",
        },
        {
          role: "assistant",
          text: "✓ Analysis exported. Paid search CAC is down 14%, partner leads convert best, and the dashboard is updated.",
        },
      ],
    },
  },
  {
    title: "Deployer X",
    category: "DevOps",
    author: "Launch Control",
    description:
      "Zero-downtime deployment tool for Docker and Kubernetes. Features blue-green deployments, automatic rollbacks, and Slack notifications. Supports AWS, GCP, and Azure.",
    rating: "4.0",
    reviewCount: "2",
    installs: "67",
    productId: "marketing-home-deployer-x",
    priceLabel: "$19",
    priceAmount: 19,
    slug: "deployer-x",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "deployer-x",
      statusLabel: "live",
      messages: [
        {
          role: "user",
          text: "Deploy the API to production with blue-green rollout and roll back if error rate spikes above 1%.",
        },
        {
          role: "assistant",
          text: "Provisioning the green environment, shifting traffic gradually, and watching latency plus error thresholds...",
        },
        {
          role: "assistant",
          text: "✓ Deploy finished. Traffic is fully on green, health checks stayed clean, and rollback hooks remain armed.",
        },
      ],
    },
  },
  {
    title: "ML Pipeline Studio",
    category: "AI / ML",
    author: "Model Harbor",
    description:
      "End-to-end ML pipeline builder with experiment tracking. Features AutoML, hyperparameter tuning, and model deployment. Integrates with MLflow, Weights & Biases, and TensorBoard.",
    rating: "4.7",
    reviewCount: "1",
    installs: "56",
    productId: "marketing-home-ml-pipeline-studio",
    priceLabel: "$39",
    priceAmount: 39,
    slug: "ml-pipeline-studio",
    imageUrl: "/images/easyclaw/step-1-models.webp",
    mediaPreview: {
      kind: "agent-conversation",
      agentName: "ml-pipeline-studio",
      statusLabel: "live",
      messages: [
        {
          role: "user",
          text: "Train three churn models, compare uplift, and publish the best run to staging with its metrics.",
        },
        {
          role: "assistant",
          text: "Launching the experiment queue, tracking feature drift, and benchmarking each run against the baseline...",
        },
        {
          role: "assistant",
          text: "✓ Best model published. Churn recall improved by 11%, artifacts are versioned, and staging endpoints are live.",
        },
      ],
    },
  },
];

export const marketplaceCategories = [
  { label: "Automation", icon: "zap" },
  { label: "Data Analysis", icon: "chart-column" },
  { label: "DevOps", icon: "wrench" },
  { label: "Web Scraping", icon: "globe" },
  { label: "AI / ML", icon: "bot" },
  { label: "Testing", icon: "test-tube" },
  { label: "Other", icon: "package" },
];
